/**
 * The machines this console drives.
 *
 * Adding one is a form and a button rather than an SSH session and a restart,
 * which is only possible because the node list lives in the router's database
 * instead of its environment. That one decision is what makes the rest of this
 * page feel like nothing.
 *
 * Two things here are deliberate and easy to lose in a tidy-up:
 *
 *   Test before Add. "Nothing answered", "the token was refused" and "something
 *   answered but it is not a node" send you to three different places. A form
 *   that says only "failed" makes you guess between them.
 *
 *   Removal names what it takes. Removing the only machine holding an embedder
 *   breaks every client using it, silently, and an hour later nobody connects
 *   that to a button they pressed.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useLive } from "./live";

import {
  addNode,
  ApiError,
  fetchConflicts,
  fetchNodes,
  mib,
  nodeImpact,
  removeNode,
  testNode,
  type NodeProbe,
  type NodeView,
} from "./api";

export default function NodesPage() {
  const queryClient = useQueryClient();
  // Reachability does change on its own — a machine goes down without telling
  // anyone. It rides the same setting as the running models, since it is the
  // same kind of fact and the same cheap call on the router.
  const nodes = useQuery({
    queryKey: ["nodes"],
    queryFn: fetchNodes,
    refetchInterval: useLive("live_running"),
  });
  const conflicts = useQuery({
    queryKey: ["nodes", "conflicts"],
    queryFn: fetchConflicts,
    // Only worth asking once there is more than one machine to disagree.
    enabled: (nodes.data?.length ?? 0) > 1,
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ["nodes"] });
    void queryClient.invalidateQueries({ queryKey: ["ollama"] });
  };

  // A node with no nodes says so, rather than showing an empty list that reads
  // as "none configured yet" and sends someone looking for an Add button.
  const notARouter =
    nodes.error instanceof ApiError && nodes.error.status === 400;

  return (
    <main>
      <div className="page-head">
        <div>
          <h1>Nodes</h1>
          <p>The machines that hold the models and do the work.</p>
        </div>
      </div>

      {notARouter ? (
        <div className="notice">
          <h3>This machine serves models itself</h3>
          <p>{nodes.error instanceof ApiError ? nodes.error.detail : ""}</p>
        </div>
      ) : nodes.error ? (
        <div className="notice">
          <h3>Cannot list nodes</h3>
          <p>{nodes.error instanceof ApiError ? nodes.error.detail : String(nodes.error)}</p>
        </div>
      ) : (
        <>
          {(conflicts.data?.length ?? 0) > 0 && (
            <div className="notice warn">
              <h3>The same name means different things</h3>
              <p>
                These are on more than one machine, and the copies are not
                identical. A request could land on either.
              </p>
              <ul className="conflicts">
                {conflicts.data!.map((c) => (
                  <li key={c.model}>
                    <b>{c.model}</b> — {c.reason} on {c.nodes.join(", ")}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="card">
            <header>
              <h2>Connected machines</h2>
              <div className="r">
                <span className="hint">
                  {nodes.data ? `${nodes.data.length} configured` : ""}
                </span>
              </div>
            </header>

            {nodes.isLoading ? (
              <div className="empty">Loading…</div>
            ) : nodes.data && nodes.data.length > 0 ? (
              <div className="tbl-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Node</th>
                      <th>State</th>
                      <th className="right">Models</th>
                      <th className="right">Loaded</th>
                      <th className="right">Free VRAM</th>
                      <th className="right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {nodes.data.map((n) => (
                      <NodeRow key={n.name} node={n} onChanged={refresh} />
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="empty">
                No machines yet. Add one below and it starts serving straight away.
              </div>
            )}

            <div className="card-foot">
              <AddNode onAdded={refresh} />
            </div>
          </div>

          <div className="info">
            A machine that stops answering goes amber and its models drop out of
            routing. It is never removed on its own — a thirty-second outage
            should not lose the configuration.
          </div>
        </>
      )}
    </main>
  );
}

function NodeRow({ node, onChanged }: { node: NodeView; onChanged: () => void }) {
  const [problem, setProblem] = useState<string | null>(null);

  const drop = useMutation({
    mutationFn: async () => {
      // Ask what it takes with it BEFORE the confirm, so the warning names the
      // actual models. A generic "are you sure" tells nobody anything.
      const impact = await nodeImpact(node.name);
      const only = impact.orphaned;
      const warning =
        only.length > 0
          ? `\n\nOnly on this machine, so they stop working:\n  ${only.join("\n  ")}`
          : "";
      if (
        !confirm(
          `Remove ${node.name}?\n\nRouting stops immediately. Nothing is ` +
            `uninstalled — its models stay on that machine and come back if ` +
            `you add it again.${warning}`,
        )
      ) {
        return null;
      }
      return removeNode(node.name);
    },
    onError: (e) => setProblem(e instanceof ApiError ? e.detail : String(e)),
    onSuccess: (result) => result && onChanged(),
  });

  return (
    <tr>
      <td>
        <div className="mname">
          <b>{node.name}</b>
          <span>{node.url}</span>
        </div>
        {!node.verify_tls && (
          <p className="unverified" title="Its certificate is not checked, so nothing proves this is the machine you meant">
            certificate not checked
          </p>
        )}
        {node.note && <p className="hint">{node.note}</p>}
        {problem && <p className="problem">{problem}</p>}
      </td>
      <td>
        {!node.enabled ? (
          <span className="pill idle"><i />Off</span>
        ) : node.reachable ? (
          <span className="pill loaded"><i />Answering</span>
        ) : (
          <span className="pill busy" title={node.error ?? undefined}>
            <i />
            {node.error ?? "Not answering"}
          </span>
        )}
      </td>
      <td className="right num">{node.reachable ? node.model_count : "—"}</td>
      <td className="right num">{node.reachable ? node.loaded_count : "—"}</td>
      <td className="right num">
        {node.free_vram_mib != null ? mib(node.free_vram_mib) : "—"}
      </td>
      <td className="right">
        <div className="rowacts">
          <button
            className="btn sm danger"
            type="button"
            disabled={drop.isPending}
            onClick={() => drop.mutate()}
          >
            {drop.isPending ? "…" : "Remove"}
          </button>
        </div>
      </td>
    </tr>
  );
}

function AddNode({ onAdded }: { onAdded: () => void }) {
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [token, setToken] = useState("");
  const [note, setNote] = useState("");
  const [verify, setVerify] = useState(true);
  const [probe, setProbe] = useState<NodeProbe | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  const clear = () => {
    setName(""); setUrl(""); setToken(""); setNote("");
    setVerify(true); setProbe(null); setProblem(null);
  };

  const test = useMutation({
    mutationFn: () => testNode(url.trim(), token.trim(), verify),
    onMutate: () => { setProbe(null); setProblem(null); },
    onSuccess: setProbe,
    onError: (e) => setProblem(e instanceof ApiError ? e.detail : String(e)),
  });

  const save = useMutation({
    mutationFn: () =>
      addNode(name.trim(), url.trim(), token.trim(), note.trim(), verify),
    onMutate: () => setProblem(null),
    onSuccess: () => { clear(); onAdded(); },
    onError: (e) => setProblem(e instanceof ApiError ? e.detail : String(e)),
  });

  const ready = name.trim() && url.trim() && token.trim();

  return (
    <div className="addnode">
      <h3>Add a machine</h3>
      <div className="addgrid">
        <label>
          Name
          <input
            value={name}
            placeholder="gpu-1"
            onChange={(e) => setName(e.target.value)}
          />
        </label>
        <label>
          Address
          <input
            value={url}
            placeholder="https://models.example.com"
            onChange={(e) => { setUrl(e.target.value); setProbe(null); }}
          />
        </label>
        <label>
          Token
          <input
            type="password"
            value={token}
            placeholder="its MODELS_NODE_TOKEN"
            onChange={(e) => { setToken(e.target.value); setProbe(null); }}
          />
        </label>
        <label>
          Note <span className="unit">optional</span>
          <input
            value={note}
            placeholder="the one under the desk"
            onChange={(e) => setNote(e.target.value)}
          />
        </label>
      </div>

      {/* Below the fields and above the buttons, so it is read before Add is
          pressed rather than discovered afterwards. Unticking it says what is
          given up in the same breath — "insecure" alone tells nobody anything. */}
      <label className="verifybox">
        <input
          type="checkbox"
          checked={verify}
          onChange={(e) => setVerify(e.target.checked)}
        />
        <span>
          Check this machine&rsquo;s certificate
          {!verify && (
            <em>
              {" "}— off. The connection is still encrypted, but nothing proves
              the machine answering is the one you meant, and this node&rsquo;s
              token is sent on every request. Only for a machine you control on
              a network you trust.
            </em>
          )}
        </span>
      </label>

      <div className="addfoot">
        <button
          className="btn"
          type="button"
          disabled={!url.trim() || !token.trim() || test.isPending}
          onClick={() => test.mutate()}
        >
          {test.isPending ? "Trying…" : "Test"}
        </button>
        <button
          className="btn primary"
          type="button"
          disabled={!ready || save.isPending}
          onClick={() => save.mutate()}
        >
          {save.isPending ? "Adding…" : "Add"}
        </button>

        {probe && (
          <span className={probe.ok ? "probe ok" : "probe bad"}>{probe.detail}</span>
        )}
        {problem && <span className="probe bad">{problem}</span>}
      </div>

      <p className="hint">
        The token is whatever that machine has set as{" "}
        <code>MODELS_NODE_TOKEN</code>. It is stored encrypted and never shown
        again — replace it here if it changes.
      </p>
    </div>
  );
}
